import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	EmployeeSize,
	CustomerInfoSaveModel,
	CustomerType,
	Industry,
	SubIndustry,
	BillingEnvironmentType
} from "client/Api/Licensing/Billing/billing";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import {
	Form,
	FormItem,
	MultiSelect,
	MultiSelectOption,
	Select,
	SelectItem,
	TextInput,
	ValidationError
} from "client/Components";
import {
	isNil,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";
import { NumberFormatter, useNumberFormatter } from "client/Hooks";
import { IValueInputAttachable } from "client/Forms/attachable";
import { IObjectFormReducer } from "client/Forms/reducer";

export type CustomerSectionProps = {
	attachable: IValueInputAttachable<BillingFormData>;
	hasWidthConstrained?: boolean;
	canSelectEnvironmentType?: boolean;
};

export const CustomerSectionForm = (props: CustomerSectionProps): ReactElement => {
	const {
		attachable,
		hasWidthConstrained = false,
		canSelectEnvironmentType = false
	} = props;

	const companyEmployeeSizeOptions: readonly SelectItem<EmployeeSize>[] = useCompanyEmployeeSizeOptions();

	return (
		<Form
			onSubmit="NONE"
			hasWidthConstrained={hasWidthConstrained}
		>
			<FormItem
				label={localize("_CompanyName")}
				isRequired
			>
				<TextInput
					{...attachable.for("name").attachToTextInput()}
					placeholder={localize("_EnterCompanyName")}
				/>

				<ValidationError {...attachable.for("name").attachToValidationError()} />
			</FormItem>
			<FormItem label={localize("_ReferenceNumber")}>
				<TextInput
					{...attachable.for("referenceNumber").attachToTextInput()}
					placeholder={localize("_EnterReferenceNumber")}
				/>

				<ValidationError {...attachable.for("referenceNumber").attachToValidationError()} />
			</FormItem>
			<FormItem
				label={localize("_CompanyType")}
				isRequired
			>
				<Select
					{...attachable.for("customerType").attachToSelect()}
					options={customerTypeOptions}
					placeholder={localize("_SelectCompanyType")}
					menuTitle={localize("_CompanyType")}
				/>

				<ValidationError {...attachable.for("customerType").attachToValidationError()} />
			</FormItem>

			<IndustrySection
				attachable={attachable}
			/>
			<FormItem
				label={localize("_CompanyEmployeeSize")}
				isRequired
			>
				<Select
					{...attachable.for("employeeSize").attachToSelect()}
					options={companyEmployeeSizeOptions}
					placeholder={localize("_SelectCompanyEmployeeSize")}
					menuTitle={localize("_CompanyEmployeeSize")}
				/>

				<ValidationError {...attachable.for("employeeSize").attachToValidationError()} />
			</FormItem>
			{canSelectEnvironmentType && (
				<FormItem
					label={localize("_EnvironmentType")}
				>
					<Select
						{...attachable.for("environmentType").attachToSelect()}
						options={environmentTypeOptions}
						placeholder={localize("_EnvironmentType")}
						menuTitle={localize("_EnvironmentType")}
					/>

					<ValidationError {...attachable.for("environmentType").attachToValidationError()} />
				</FormItem>
			)}
		</Form>
	);
};

CustomerSectionForm.displayName = "CustomerSectionForm";

type EmployeeSizeRange = {
	min: number;
	max?: number;
	value: EmployeeSize;
};

const employeeSizeRanges: EmployeeSizeRange[] = [
	{
		min: 1,
		max: 10,
		value: EmployeeSize._1_10
	},
	{
		min: 11,
		max: 50,
		value: EmployeeSize._11_50
	},
	{
		min: 51,
		max: 200,
		value: EmployeeSize._51_200
	},
	{
		min: 201,
		max: 500,
		value: EmployeeSize._201_500
	},
	{
		min: 501,
		max: 1000,
		value: EmployeeSize._501_1000
	},
	{
		min: 1001,
		max: 5000,
		value: EmployeeSize._1001_5000
	},
	{
		min: 5001,
		max: 10000,
		value: EmployeeSize._5001_10000
	},
	{
		min: 10001,
		value: EmployeeSize.MoreThan10000
	}
];
const getCompanyEmployeeSizeOptionText = (min: string, max: string): string => {
	return resolveFormattedTextString({
		template: localize("_NumberRangeFormat"),
		replacements: [
			{
				what: "{{Min}}",
				filler: min
			},
			{
				what: "{{Max}}",
				filler: max
			}
		]
	});
};

const currentCulture: string = getCurrentCulture();

type IndustrySectionProps = {
	attachable: IValueInputAttachable<BillingFormData>;
};

const IndustrySection = (props: IndustrySectionProps): ReactElement => {
	const {
		attachable
	} = props;

	const selectedIndustry: Industry | null = attachable.value
		.industry;

	const changeSubIndustryHandler = useCallback(
		(newValue: readonly SubIndustry[]): void => {
			const industry: Industry | null = attachable.for("industry")
				.value;

			if (isNil(industry) || !isIndustryWithSubIndustries(industry)) {
				return;
			}

			const industriesWithOtherSubIndustrySelectedReducer: IObjectFormReducer<ReadonlySet<IndustryWithSubIndustries>> = attachable
				.for("industriesWithOtherSelected")
				.reducer;

			const industriesWithOtherSubIndustrySelected = new Set<IndustryWithSubIndustries>(
				industriesWithOtherSubIndustrySelectedReducer.value
			);

			if (newValue.includes(SubIndustry.Other)) {
				industriesWithOtherSubIndustrySelected.add(industry);
			} else {
				industriesWithOtherSubIndustrySelected.delete(industry);
			}

			industriesWithOtherSubIndustrySelectedReducer
				.setValue(
					industriesWithOtherSubIndustrySelected
				);
		},
		[attachable]
	);

	const changeIndustryHandler = useCallback(
		(newValue: Industry | null): void => {
			if (isNil(newValue) || !isIndustryWithSubIndustries(newValue)) {
				return;
			}

			const shouldOtherBeSelectedInSubIndustries: boolean = attachable
				.for("industriesWithOtherSelected")
				.value
				.has(newValue);

			const newSubIndustriesValue: SubIndustry[] = [
				...attachable
					.for("subIndustries")
					.value
					.filter(x => x !== SubIndustry.Other)
			];

			if (shouldOtherBeSelectedInSubIndustries) {
				newSubIndustriesValue.push(SubIndustry.Other);
			}

			attachable.for("subIndustries")
				.reducer
				.setValue(
					newSubIndustriesValue
				);
		},
		[attachable]
	);

	return (
		<>
			<FormItem
				label={localize("_Industry")}
				isRequired
			>
				<Select
					{...attachable.for("industry")
						.attachToSelect()
						.withOnChange(changeIndustryHandler)}
					options={industryOptions}
					placeholder={localize("_SelectIndustry")}
					menuTitle={localize("_Industry")}
				/>

				<ValidationError {...attachable.for("industry").attachToValidationError()} />
			</FormItem>

			{isNotNil(selectedIndustry)
				&& isIndustryWithSubIndustries(selectedIndustry)
				&& (
					<FormItem
						label={localize("_SubIndustries")}
						isRequired
					>
						<MultiSelect
							{...attachable.for("subIndustries")
								.attachToMultiSelect()
								.withOnChange(changeSubIndustryHandler)}
							options={subIndustriesOptionsPerIndustry[selectedIndustry]}
							placeholder={localize("_SelectSubIndustries")}
							menuTitle={localize("_SubIndustries")}
						/>

						<ValidationError {...attachable.for("subIndustries").attachToValidationError()} />
					</FormItem>
				)
			}

			{attachable.value.industry === Industry.Other && (
				<FormItem
					label={localize("_OtherIndustry")}
					isRequired
				>
					<TextInput
						{...attachable.for("otherIndustry").attachToTextInput()}
						placeholder={localize("_EnterOtherIndustry")}
					/>

					<ValidationError {...attachable.for("otherIndustry").attachToValidationError()} />
				</FormItem>
			)}
		</>
	);
};
IndustrySection.displayName = "IndustrySection";

function useCompanyEmployeeSizeOptions(): readonly SelectItem<EmployeeSize>[] {
	const thousandsFormatter: NumberFormatter = useNumberFormatter({
		culture: currentCulture,
		format: "thousand-separator"
	});

	return useMemo(
		(): readonly SelectItem<EmployeeSize>[] => {
			return employeeSizeRanges.map(
				(x: EmployeeSizeRange): SelectItem<EmployeeSize> => {
					if (isNotNil(x.max)) {
						return {
							text: getCompanyEmployeeSizeOptionText(
								thousandsFormatter(x.min),
								thousandsFormatter(x.max)
							),
							value: x.value
						};
					}

					return {
						text: resolveFormattedTextString({
							template: localize("_OverAmount"),
							replacements: [
								{
									what: "{{Amount}}",
									filler: thousandsFormatter(x.min)
								}
							]
						}),
						value: x.value
					};
				}
			);
		},
		[thousandsFormatter]
	);
}

const industryOptions: SelectItem<Industry>[] = [
	{
		text: localize("_Agriculture"),
		value: Industry.Agriculture
	},
	{
		text: localize("_ChemicalManufacturing"),
		value: Industry.ChemicalManufacturing
	},
	{
		text: localize("_Construction"),
		value: Industry.Construction
	},
	{
		text: localize("_Creative"),
		value: Industry.Creative
	},
	{
		text: localize("_Consulting"),
		value: Industry.Consulting
	},
	{
		text: localize("_Defense"),
		value: Industry.Defense
	},
	{
		text: localize("_Education"),
		value: Industry.Education
	},
	{
		text: localize("_Energy"),
		value: Industry.Energy
	},
	{
		text: localize("_EntertainmentAndMedia"),
		value: Industry.EntertainmentAndMedia
	},
	{
		text: localize("_FinancialServices"),
		value: Industry.FinancialServices
	},
	{
		text: localize("_FoodAndBeverage"),
		value: Industry.FoodAndBeverage
	},
	{
		text: localize("_Government"),
		value: Industry.Government
	},
	{
		text: localize("_Healthcare"),
		value: Industry.Healthcare
	},
	{
		text: localize("_Hospitality"),
		value: Industry.Hospitality
	},
	{
		text: localize("_IT"),
		value: Industry.InformationTechnology
	},
	{
		text: localize("_Legal"),
		value: Industry.Legal
	},
	{
		text: localize("_Manufacturing"),
		value: Industry.Manufacturing
	},
	{
		text: localize("_Retail"),
		value: Industry.Retail
	},
	{
		text: localize("_Software"),
		value: Industry.Software
	},
	{
		text: localize("_Telecom"),
		value: Industry.Telecom
	},
	{
		text: localize("_Transport"),
		value: Industry.Transport
	},
	{
		text: localize("_Other"),
		value: Industry.Other
	}
];

const getOtherIndustryOptionText = (industryKey: LocalizationKey): string => {
	return resolveFormattedTextString({
			template: localize("_OtherIndustryName"),
			replacements: [
				{
					what: "{{Industry}}",
					filler: localize(industryKey)
				}
			]
	});
};

export const subIndustriesOptionsPerIndustry: {
	[key in IndustryWithSubIndustries]: readonly MultiSelectOption<SubIndustry>[];
} = {
	[Industry.Education]: [
		{
			text: localize("_PrimaryEducation"),
			value: SubIndustry.PrimaryEducation
		},
		{
			text: localize("_SecondaryEducation"),
			value: SubIndustry.SecondaryEducation
		},
		{
			text: localize("_HigherEducation"),
			value: SubIndustry.HigherEducation
		},
		{
			text: getOtherIndustryOptionText("_Education"),
			value: SubIndustry.Other
		}
	],
	[Industry.FinancialServices]: [
		{
			text: localize("_Accounting"),
			value: SubIndustry.Accounting
		},
		{
			text: localize("_CommercialBanking"),
			value: SubIndustry.CommercialBanking
		},
		{
			text: localize("_Insurance"),
			value: SubIndustry.Insurance
		},
		{
			text: getOtherIndustryOptionText("_FinancialServices"),
			value: SubIndustry.Other
		}
	],
	[Industry.Government]: [
		{
			text: localize("_MinistriesNationalLevelOrg"),
			value: SubIndustry.Ministries
		},
		{
			text: localize("_ProvincesRegionalStateLevelOrg"),
			value: SubIndustry.Provinces
		},
		{
			text: localize("_MunicipalitiesLocalLevelOrg"),
			value: SubIndustry.Municipalities
		},
		{
			text: localize("_Utilities"),
			value: SubIndustry.GovernmentUtilities
		},
		{
			text: getOtherIndustryOptionText("_Government"),
			value: SubIndustry.Other
		}
	],
	[Industry.Healthcare]: [
		{
			text: localize("_ElderCare"),
			value: SubIndustry.ElderCare
		},
		{
			text: localize("_DisabilityCare"),
			value: SubIndustry.DisabilityCare
		},
		{
			text: localize("_RehabilitationCare"),
			value: SubIndustry.RehabilitationCare
		},
		{
			text: localize("_YouthCare"),
			value: SubIndustry.YouthCare
		},
		{
			text: localize("_MentalHealthCare"),
			value: SubIndustry.MentalHealthcare
		},
		{
			text: localize("_Childcare"),
			value: SubIndustry.Childcare
		},
		{
			text: localize("_Hospital"),
			value: SubIndustry.Hospital
		},
		{
			text: localize("_Physio"),
			value: SubIndustry.Physio
		},
		{
			text: localize("_GeneralPractitioners"),
			value: SubIndustry.GeneralPractitioners
		},
		{
			text: getOtherIndustryOptionText("_Healthcare"),
			value: SubIndustry.Other
		},
	]
};

const customerTypeOptions: SelectItem<CustomerType>[] = [
	{
		text: localize("_Commercial"),
		value: CustomerType.Commercial
	},
	{
		text: localize("_Education"),
		value: CustomerType.Education
	},
	{
		text: localize("_Nonprofit"),
		value: CustomerType.Nonprofit
	}
];

const industriesThatRequireSubIndustries: ReadonlySet<Industry> = new Set(
	[
		Industry.Government,
		Industry.Education,
		Industry.Healthcare,
		Industry.FinancialServices,
	]
);

export function isIndustryWithSubIndustries(
	industry: Industry
): industry is IndustryWithSubIndustries {
	return industriesThatRequireSubIndustries.has(industry);
}

const environmentTypeOptions: SelectItem<BillingEnvironmentType>[] = [
	{
		text: localize("_Default"),
		value: BillingEnvironmentType.Default
	},
	{
		text: localize("_Demo"),
		value: BillingEnvironmentType.Demo
	},
	{
		text: localize("_NotForResale"),
		value: BillingEnvironmentType.NotForResale
	}
];

export type IndustryWithSubIndustries = Industry.Government
	| Industry.Education
	| Industry.Healthcare
	| Industry.FinancialServices;

export type BillingFormData = Omit<
	CustomerInfoSaveModel,
	"subIndustries"
> & {
	subIndustries: readonly SubIndustry[];
	industriesWithOtherSelected: ReadonlySet<IndustryWithSubIndustries>;
};

export function getSubIndustriesOfIndustry(
	industry: IndustryWithSubIndustries,
	subIndustries: readonly SubIndustry[]
): readonly SubIndustry[] {
	return subIndustriesOptionsPerIndustry[industry].map(
		x => x.value
	)
		.filter(
			x => subIndustries.includes(x)
		);
}
